import {
  Badge,
  Button,
  EmptyState,
  ErrorBanner,
  Table,
} from "frontend-kit/components/index.js";
import { duskAmountNode } from "./dusk-amount.js";
import {
  getAchievementStats,
  getAgentSnapshot,
  listMarketsByCommitment,
  loadBackendConfig,
} from "./gateway-api.js";
import { maskCommitment } from "./privacy-copy.js";
import {
  effectiveStatus,
  isLiveStatus,
} from "./market-status.js";

const AMBIT_DOCS_URL = "https://docs.nocturne-standards.org/v1/ambit/";

/**
 * @param {{ session?: { state?: string } }} snapshot
 */
export function sessionBadgeVariant(snapshot) {
  const state = String(snapshot?.session?.state || "unknown").toLowerCase();
  if (state === "live") return { variant: "success", label: "Live" };
  if (state === "expired") return { variant: "neutral", label: "Expired" };
  if (state === "revoked") return { variant: "danger", label: "Revoked" };
  return { variant: "neutral", label: "Unknown" };
}

function facilityBar(label, valueLux, capLux) {
  const wrap = document.createElement("div");
  wrap.className = "agent-facility-row";
  const head = document.createElement("div");
  head.className = "agent-facility-head";
  const title = document.createElement("span");
  title.className = "stat-label dt-th";
  title.textContent = label;
  const value = document.createElement("span");
  value.className = "agent-facility-value";
  value.append(duskAmountNode(valueLux));
  if (capLux != null) {
    value.append(" / ");
    value.append(duskAmountNode(capLux));
  }
  head.append(title, value);
  wrap.append(head);
  if (capLux != null) {
    try {
      const pct = capLux > 0n ? Math.min(100, Number((valueLux * 100n) / capLux)) : 0;
      const track = document.createElement("div");
      track.className = "agent-facility-track";
      const fill = document.createElement("div");
      fill.className = "agent-facility-fill";
      fill.style.width = `${pct}%`;
      track.append(fill);
      wrap.append(track);
    } catch {
      /* ignore bad bigint */
    }
  }
  return wrap;
}

function wrongRouteBanner({ commitmentHex }) {
  const banner = document.createElement("div");
  banner.className = "wrong-route-banner dt-card";
  const p = document.createElement("p");
  p.textContent =
    "This commitment holds human achievement NFTs. Open the trader profile instead of the agent monitor.";
  banner.append(
    p,
    Button({
      label: "Open trader profile",
      variant: "secondary",
      onClick: () => {
        location.hash = `#/trader/${commitmentHex}`;
      },
    })
  );
  return banner;
}

function buildPositionRows(markets) {
  return markets
    .map((market) => {
      const yes = Number(market.yes_shares ?? market.yes ?? 0);
      const no = Number(market.no_shares ?? market.no ?? 0);
      if (yes <= 0 && no <= 0) return null;
      const status = effectiveStatus(market);
      return {
        market_id: Number(market.market_id),
        title: market.metadata?.title ?? `Event #${market.market_id}`,
        status,
        yes,
        no,
        live: isLiveStatus(status),
      };
    })
    .filter(Boolean);
}

/**
 * @param {{ commitmentHex: string, log?: Function, onOpenMarket?: Function }} opts
 */
export async function renderAgentPage({ commitmentHex, log, onOpenMarket }) {
  const root = document.createElement("div");
  root.className = "agent-page";

  const header = document.createElement("div");
  header.className = "agent-header";
  const h1 = document.createElement("h1");
  h1.textContent = "Agent profile";
  const meta = document.createElement("p");
  meta.className = "hint agent-commitment mono";
  meta.textContent = maskCommitment(commitmentHex);
  meta.title = commitmentHex;
  header.append(h1, meta);
  root.append(header);

  const bannerHost = document.createElement("div");
  bannerHost.className = "agent-banner-host";
  root.append(bannerHost);

  const facility = document.createElement("section");
  facility.className = "agent-facility dt-card";
  root.append(facility);

  const positions = document.createElement("section");
  positions.className = "agent-positions dt-card";
  const positionsTitle = document.createElement("h2");
  positionsTitle.className = "agent-section-title";
  positionsTitle.textContent = "Positions";
  positions.append(positionsTitle);
  const positionsHost = document.createElement("div");
  positions.append(positionsHost);
  root.append(positions);

  const footer = document.createElement("section");
  footer.className = "agent-footer dt-card";
  const footerCopy = document.createElement("p");
  footerCopy.className = "hint";
  footerCopy.textContent =
    "Ambit agents run locally via the CLI. This portal is read-only - no mandate issue, revoke, or tick controls.";
  const docsLink = document.createElement("a");
  docsLink.href = AMBIT_DOCS_URL;
  docsLink.target = "_blank";
  docsLink.rel = "noopener noreferrer";
  docsLink.className = "link-btn";
  docsLink.textContent = "Ambit docs";
  footer.append(footerCopy, docsLink);
  root.append(footer);

  const cfg = loadBackendConfig();
  let snapshot = null;
  let positionRows = [];
  let err = null;
  let loading = true;

  function paintFacility() {
    facility.replaceChildren();
    if (loading) {
      facility.append(
        Object.assign(document.createElement("p"), {
          className: "hint",
          textContent: "Loading agent facility…",
        })
      );
      return;
    }
    if (err) {
      facility.append(ErrorBanner({ message: err }));
      return;
    }
    if (!snapshot) {
      facility.append(
        EmptyState({
          title: "No agent snapshot",
          description: "Indexer has no facility data for this commitment yet.",
        })
      );
      return;
    }

    const badges = document.createElement("div");
    badges.className = "state-row agent-badges";
    badges.append(Badge({ variant: "neutral", label: snapshot.trader_class || "agent" }));
    badges.append(Badge(sessionBadgeVariant(snapshot)));
    facility.append(badges);

    const deployed = BigInt(snapshot.deployed_lux || "0");
    const fees = BigInt(snapshot.fees_paid_lux || "0");
    const spendCap =
      snapshot.spend_cap_lux != null && snapshot.spend_cap_lux !== ""
        ? BigInt(snapshot.spend_cap_lux)
        : null;
    facility.append(facilityBar("Deployed", deployed, spendCap));

    const feesRow = document.createElement("div");
    feesRow.className = "agent-facility-row";
    const feesHead = document.createElement("div");
    feesHead.className = "agent-facility-head";
    feesHead.append(
      Object.assign(document.createElement("span"), {
        className: "stat-label dt-th",
        textContent: "Fees paid",
      }),
      Object.assign(document.createElement("strong"), {
        className: "stat-value dt-val",
      })
    );
    feesHead.querySelector(".stat-value").append(duskAmountNode(fees));
    feesRow.append(feesHead);
    facility.append(feesRow);

    const summary = snapshot.positions_summary;
    if (summary) {
      const summaryRow = document.createElement("p");
      summaryRow.className = "hint";
      summaryRow.textContent = `${summary.open_markets} open market(s) · `;
      summaryRow.append(duskAmountNode(summary.collateral_lux), " collateral");
      facility.append(summaryRow);
    }
  }

  function paintPositions() {
    positionsHost.replaceChildren();
    if (loading) {
      positionsHost.append(
        Object.assign(document.createElement("p"), {
          className: "hint",
          textContent: "Loading positions…",
        })
      );
      return;
    }
    if (!positionRows.length) {
      positionsHost.append(
        EmptyState({
          title: "No open positions",
          description: "Markets with shares for this commitment appear here.",
        })
      );
      return;
    }
    const scroll = document.createElement("div");
    scroll.className = "portfolio-table-scroll";
    scroll.append(
      Table({
        columns: [
          {
            header: "Event",
            render: (row) => {
              const btn = document.createElement("button");
              btn.type = "button";
              btn.className = "event-pill";
              btn.textContent = row.title;
              btn.addEventListener("click", () => onOpenMarket?.(row.market_id));
              return btn;
            },
          },
          {
            header: "Status",
            render: (row) => Badge({ variant: row.live ? "success" : "neutral", label: row.status }),
          },
          { header: "YES", render: (row) => String(row.yes) },
          { header: "NO", render: (row) => String(row.no) },
        ],
        rows: positionRows,
      })
    );
    positionsHost.append(scroll);
  }

  async function load() {
    loading = true;
    err = null;
    paintFacility();
    paintPositions();
    try {
      const [snapshotResult, statsResult, marketsResult] = await Promise.allSettled([
        getAgentSnapshot(cfg, commitmentHex),
        getAchievementStats(cfg, commitmentHex),
        listMarketsByCommitment(cfg, commitmentHex),
      ]);

      if (snapshotResult.status === "fulfilled") {
        snapshot = snapshotResult.value;
        log?.(
          `agent snapshot deployed=${snapshot.deployed_lux} open=${snapshot.positions_summary?.open_markets ?? 0}`
        );
      } else {
        const msg = snapshotResult.reason?.message || String(snapshotResult.reason);
        if (msg.includes("404")) {
          snapshot = null;
          err = null;
        } else {
          snapshot = null;
          err = msg;
        }
      }

      bannerHost.replaceChildren();
      if (statsResult.status === "fulfilled" && statsResult.value?.held_types?.length > 0) {
        bannerHost.append(wrongRouteBanner({ commitmentHex }));
      }

      if (marketsResult.status === "fulfilled") {
        positionRows = buildPositionRows(marketsResult.value);
      } else {
        positionRows = [];
        log?.(`agent positions error: ${marketsResult.reason?.message || marketsResult.reason}`);
      }
    } finally {
      loading = false;
      paintFacility();
      paintPositions();
    }
  }

  await load();
  return root;
}
